import { prefersReducedMotion } from './motion';

/** Amber current, hot current and steel — sparks off the board's own circuit. */
const COLORS = ['#FFC94D', '#FFE8A3', '#FFC94D', '#7E92B0'];

/** The engineers' variant: the same shower burning hotter, not bigger. */
const HOT_COLORS = ['#FFE8A3', '#FFC94D', '#FFE8A3', '#7E92B0'];

const PARTICLES = 110;
const GRAVITY = 900; // px/s²
const DRAG = 0.86;
const LIFETIME = 2600; // ms

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  /** Half-length of the strip, in px. */
  size: number;
  spin: number;
  angle: number;
  color: string;
}

/**
 * A one-shot burst of sparks for the perfect grid.
 *
 * Runs on its own full-screen canvas so it can spill outside the board, tears
 * itself down when the last particle dies, and does nothing at all when the
 * player has asked for reduced motion.
 */
export function celebrate(origin?: { x: number; y: number }, opts: { hot?: boolean } = {}): void {
  if (prefersReducedMotion()) return;
  if (typeof document === 'undefined' || !document.body) return;

  const canvas = document.createElement('canvas');
  canvas.className = 'confetti';
  canvas.setAttribute('aria-hidden', 'true');
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const dpr = Math.min(globalThis.devicePixelRatio || 1, 2);
  const width = globalThis.innerWidth;
  const height = globalThis.innerHeight;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  ctx.scale(dpr, dpr);
  document.body.appendChild(canvas);

  const from = origin ?? { x: width / 2, y: height * 0.38 };
  const palette = opts.hot ? HOT_COLORS : COLORS;
  const particles: Particle[] = Array.from({ length: PARTICLES }, () => {
    // Bias upward and outward, like a spark shower off a conductor.
    const angle = -Math.PI / 2 + (Math.random() - 0.5) * 2.7;
    const speed = 340 + Math.random() * 560;
    return {
      // Spawn across a band rather than a point, so it reads as a shower
      // rather than one dense puff.
      x: from.x + (Math.random() - 0.5) * 150,
      y: from.y + (Math.random() - 0.5) * 40,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 3 + Math.random() * 5,
      spin: (Math.random() - 0.5) * 14,
      angle: Math.random() * Math.PI,
      color: palette[Math.floor(Math.random() * palette.length)],
    };
  });

  let last = performance.now();
  const started = last;
  let frame = 0;

  const tick = (now: number) => {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    const age = (now - started) / LIFETIME;

    ctx.clearRect(0, 0, width, height);
    ctx.globalAlpha = Math.max(0, 1 - age * age);

    for (const p of particles) {
      p.vy += GRAVITY * dt;
      // Frame-rate independent air drag, so 120 Hz doesn't slow the sparks.
      const drag = Math.pow(DRAG, dt * 60);
      p.vx *= drag;
      p.vy *= drag;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.angle += p.spin * dt;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      ctx.fillStyle = p.color;
      ctx.fillRect(-p.size / 2, -p.size, p.size, p.size * 2);
      ctx.restore();
    }

    if (age >= 1 || particles.every((p) => p.y - p.size > height)) {
      canvas.remove();
      return;
    }
    frame = requestAnimationFrame(tick);
  };

  frame = requestAnimationFrame(tick);

  // Never outlive the celebration, even if the tab is backgrounded and rAF
  // stops firing.
  setTimeout(() => {
    cancelAnimationFrame(frame);
    canvas.remove();
  }, LIFETIME + 400);
}
